import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { mealAPI, hasSessionToken } from '../utils/api';
import { isAuthUnchanged } from '../utils/favoritesRaceGuard';
import useUserStore from '../store/userStore';
import { toast } from 'sonner';

const FavoritesContext = createContext(null);

// P8: account-level Favorites are the SOLE source of truth once logged in.
// Legacy device-local favorites (localStorage['favoriteMeals']) are left
// untouched and are never read or imported.
export function FavoritesProvider({ children }) {
  const [favoriteIds, setFavoriteIds] = useState(() => new Set());
  const [favorites, setFavorites] = useState([]);
  const [loaded, setLoaded] = useState(false);
  // React to the authenticated user's identity (logout/login/switch happen via
  // SPA navigation WITHOUT remounting this provider).
  const userId = useUserStore((s) => (s.user ? s.user.user_id : null));
  // Generation guard: a delayed response for a previous user must NEVER overwrite
  // the current user's state.
  const genRef = useRef(0);
  // Auth epoch + current-user ref: guard async MUTATIONS (add/remove) so a
  // late response/rollback from a prior account cannot mutate another account's
  // frontend state after logout/switch.
  const authEpochRef = useRef(0);
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  // True only if the authenticated account has NOT changed since a mutation began.
  const authUnchanged = useCallback(
    (actingUserId, epoch) =>
      isAuthUnchanged(authEpochRef.current, userIdRef.current, actingUserId, epoch),
    [],
  );

  const refresh = useCallback(async () => {
    const myGen = ++genRef.current;
    if (!userId || !hasSessionToken()) {
      setFavoriteIds(new Set());
      setFavorites([]);
      setLoaded(true);
      return;
    }
    try {
      const res = await mealAPI.getFavorites();
      if (myGen !== genRef.current) return; // auth changed mid-flight -> discard
      const data = res.data || {};
      setFavoriteIds(new Set(data.favorite_ids || []));
      setFavorites(data.favorites || []);
    } catch (e) {
      if (myGen !== genRef.current) return;
      setFavoriteIds(new Set());
      setFavorites([]);
    } finally {
      if (myGen === genRef.current) setLoaded(true);
    }
  }, [userId]);

  // Auth-identity-driven (re)load. On logout/switch, immediately clear stale
  // state (so a previous account's favorites are never shown) then reload.
  useEffect(() => {
    // bump generation so any in-flight request for the prior user is ignored
    genRef.current++;
    authEpochRef.current++; // new auth epoch => stale mutations can't mutate new account
    setFavoriteIds(new Set());
    setFavorites([]);
    if (!userId) {
      setLoaded(true);
      return;
    }
    setLoaded(false);
    refresh();
  }, [userId, refresh]);

  const isFavorite = useCallback((mealId) => favoriteIds.has(mealId), [favoriteIds]);

  const addFavorite = useCallback(async (mealId) => {
    const acting = userIdRef.current;
    if (!acting) { toast.error('Please sign in to save favorites'); return false; }
    const epoch = authEpochRef.current;
    setFavoriteIds((prev) => new Set(prev).add(mealId)); // optimistic (acting user)
    try {
      const res = await mealAPI.addFavorite(mealId);
      if (!authUnchanged(acting, epoch)) return false; // account switched -> don't touch/toast
      const summary = res.data?.favorite;
      if (summary) setFavorites((prev) => (prev.some((f) => f.meal_id === mealId) ? prev : [summary, ...prev]));
      return true;
    } catch (e) {
      if (!authUnchanged(acting, epoch)) return false; // don't rollback into another account
      setFavoriteIds((prev) => { const n = new Set(prev); n.delete(mealId); return n; }); // rollback
      toast.error('Could not add favorite');
      return false;
    }
  }, [authUnchanged]);

  const removeFavorite = useCallback(async (mealId) => {
    const acting = userIdRef.current;
    if (!acting) return false;
    const epoch = authEpochRef.current;
    const prevIds = favoriteIds;
    const prevList = favorites;
    setFavoriteIds((prev) => { const n = new Set(prev); n.delete(mealId); return n; }); // optimistic
    setFavorites((prev) => prev.filter((f) => f.meal_id !== mealId));
    try {
      await mealAPI.removeFavorite(mealId);
      if (!authUnchanged(acting, epoch)) return false;
      return true;
    } catch (e) {
      if (!authUnchanged(acting, epoch)) return false; // never restore prior account's state under new one
      setFavoriteIds(prevIds); // rollback
      setFavorites(prevList);
      toast.error('Could not remove favorite');
      return false;
    }
  }, [favoriteIds, favorites, authUnchanged]);

  const toggleFavorite = useCallback(async (mealId) => {
    if (favoriteIds.has(mealId)) {
      const ok = await removeFavorite(mealId);
      if (ok) toast.success('Removed from favorites');
    } else {
      const ok = await addFavorite(mealId);
      if (ok) toast.success('Added to favorites');
    }
  }, [favoriteIds, addFavorite, removeFavorite]);

  const value = {
    favoriteIds, favorites, loaded, isFavorite,
    addFavorite, removeFavorite, toggleFavorite, refresh,
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  const ctx = useContext(FavoritesContext);
  if (!ctx) {
    // Defensive: return a no-op shape so a stray consumer never crashes.
    return {
      favoriteIds: new Set(), favorites: [], loaded: true,
      isFavorite: () => false, addFavorite: async () => false,
      removeFavorite: async () => false, toggleFavorite: async () => {}, refresh: async () => {},
    };
  }
  return ctx;
}
